import React, { useState, useEffect, useCallback } from 'react';
import texasApi from '../../services/texasApi';

export const SB38Timeline: React.FC = () => {
  const [caseId, setCaseId] = useState<string>('');
  const [timelines, setTimelines] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTimelines = useCallback(async (id: string) => {
    if (!id.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await texasApi.sb38.getCase(id.trim());
      setTimelines(res?.timelines || [res]);
    } catch (err: any) {
      setError(err?.message || 'Failed to load SB 38 timelines');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (caseId.trim()) {
      fetchTimelines(caseId.trim());
    }
  }, [caseId, fetchTimelines]);

  const statusColors: Record<string, string> = {
    trial: 'bg-blue-100 text-blue-700',
    service: 'bg-amber-100 text-amber-700',
    writ: 'bg-red-100 text-red-700',
    sworn: 'bg-purple-100 text-purple-700',
    summary: 'bg-emerald-100 text-emerald-700',
  };

  return (
    <div data-testid="sb38-timeline" className="texas-page-container">
      <h1>SB 38 Timeline Tracker</h1>
      <p className="page-description">
        Track SB 38 accelerated eviction timelines by case ID.
      </p>

      <section className="search-bar">
        <div className="field-group">
          <label htmlFor="case-id-input">Case ID</label>
          <input
            id="case-id-input"
            type="text"
            placeholder="Case ID"
            value={caseId}
            onChange={(e) => setCaseId(e.target.value)}
            data-testid="case-id-input"
          />
        </div>
        <button type="button" onClick={() => fetchTimelines(caseId)} disabled={loading} data-testid="search-btn">
          {loading ? 'Loading...' : 'Search'}
        </button>
      </section>

      {error && <div className="error" data-testid="error">{error}</div>}

      {!loading && timelines.length === 0 && caseId && (
        <div className="no-results" data-testid="no-results">
          No timelines found for case {caseId}
        </div>
      )}

      {timelines.length > 0 && (
        <section className="timeline-section" data-testid="timeline-list">
          <h2>Timeline Events</h2>
          <table className="timeline-table">
            <thead>
              <tr>
                <th>Event Type</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {timelines.map((t: any, idx: number) => (
                <tr key={idx} data-testid={`timeline-event-${idx}`}>
                  <td>{t.eventType || t.type || 'Unknown'}</td>
                  <td>{t.date || t.deadline || '—'}</td>
                  <td>
                    <span className={`status-badge ${statusColors[t.eventType] || ''}`}>
                      {t.status || 'Pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
};

export default SB38Timeline;
